import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

/**
 * Presentation primitives for the Central admin console: icons, the page
 * header, breadcrumbs and skeleton loaders. Pure markup — no data, no logic.
 */

const ICON_PATHS: Record<string, string> = {
  overview: 'M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z',
  users:
    'M16 11a4 4 0 1 0-4-4 4 4 0 0 0 4 4zm-8 0a3 3 0 1 0-3-3 3 3 0 0 0 3 3zm0 2c-2.7 0-8 1.3-8 4v2h8v-2c0-1 .4-1.9 1.1-2.6A11 11 0 0 0 8 13zm8 0c-2.7 0-8 1.3-8 4v2h16v-2c0-2.7-5.3-4-8-4z',
  workspaces: 'M4 21V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v4h2a2 2 0 0 1 2 2v10M4 21h16M8 7h4M8 11h4M8 15h4',
  tokens: 'M8 2h8a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2zm4 16h.01',
  attempts: 'M22 2 11 13M22 2l-7 20-4-9-9-4 20-7z',
  apps: 'M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z',
  billing: 'M2 7a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V7zm0 3h20M6 15h4',
  mail: 'M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zm18 2-10 7L2 6',
  home: 'M3 10.5 12 3l9 7.5V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1v-9.5z',
  logout: 'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9',
  menu: 'M4 6h16M4 12h16M4 18h16',
  close: 'M6 6l12 12M18 6 6 18',
  search: 'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm10 2-4.35-4.35',
  database: 'M12 3c4.4 0 8 1.3 8 3s-3.6 3-8 3-8-1.3-8-3 3.6-3 8-3zm8 3v12c0 1.7-3.6 3-8 3s-8-1.3-8-3V6m16 6c0 1.7-3.6 3-8 3s-8-1.3-8-3',
  apple: 'M12 2a7 7 0 0 1 7 7v6a7 7 0 0 1-14 0V9a7 7 0 0 1 7-7zm0 4v4',
  android: 'M5 9h14v8a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V9zm2-4 2 3m8-3-2 3M9 13h.01M15 13h.01',
  check: 'M20 6 9 17l-5-5',
  alert: 'M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z',
  chart: 'M3 3v18h18M7 15l4-4 3 3 5-6',
  chevron: 'M9 18l6-6-6-6',
  shield: 'M12 2 4 5v6c0 5 3.4 9.7 8 11 4.6-1.3 8-6 8-11V5l-8-3z',
};

export type IconName = keyof typeof ICON_PATHS;

export function Icon({ name, className = 'h-5 w-5' }: { name: IconName; className?: string }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d={ICON_PATHS[name]} />
    </svg>
  );
}

export interface Crumb {
  label: string;
  to?: string;
}

export function Breadcrumb({ items }: { items: Crumb[] }) {
  return (
    <nav aria-label="Breadcrumb" className="mb-2">
      <ol className="flex flex-wrap items-center gap-1 text-xs font-medium text-slate-500">
        {items.map((c, i) => (
          <li key={`${c.label}-${i}`} className="flex items-center gap-1">
            {i > 0 && <Icon name="chevron" className="h-3.5 w-3.5 text-slate-400" />}
            {c.to ? (
              <Link to={c.to} className="hover:text-emerald-700">
                {c.label}
              </Link>
            ) : (
              <span aria-current="page" className="text-slate-700">
                {c.label}
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

export function PageHeader({
  title,
  subtitle,
  breadcrumb,
  actions,
  as: Heading = 'h2',
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  breadcrumb?: Crumb[];
  actions?: ReactNode;
  as?: 'h1' | 'h2';
}) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0">
        {breadcrumb && <Breadcrumb items={breadcrumb} />}
        <Heading className="text-2xl font-semibold tracking-tight text-slate-900 break-words">{title}</Heading>
        {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

/** Skeleton rows while a table loads; still announces the loading message. */
export function TableSkeleton({ message, rows = 6, cols = 5 }: { message: string; rows?: number; cols?: number }) {
  return (
    <div role="status" aria-live="polite" className="animate-pulse">
      <span className="sr-only">{message}</span>
      <div className="mb-3 h-3 w-32 rounded bg-slate-200" />
      <div className="overflow-hidden rounded-lg border border-slate-200">
        <div className="flex gap-4 bg-slate-50 px-4 py-3">
          {Array.from({ length: cols }).map((_, i) => (
            <div key={i} className="h-2.5 flex-1 rounded bg-slate-200" />
          ))}
        </div>
        {Array.from({ length: rows }).map((_, r) => (
          <div key={r} className="flex gap-4 border-t border-slate-100 px-4 py-4">
            {Array.from({ length: cols }).map((_, i) => (
              <div key={i} className={`h-3 flex-1 rounded bg-slate-100 ${i === 0 ? 'max-w-[40%]' : ''}`} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Skeleton for detail pages and card grids. */
export function CardsSkeleton({ message, cards = 3 }: { message: string; cards?: number }) {
  return (
    <div role="status" aria-live="polite" className="animate-pulse space-y-4">
      <span className="sr-only">{message}</span>
      {Array.from({ length: cards }).map((_, i) => (
        <div key={i} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-4 h-3 w-40 rounded bg-slate-200" />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="h-3 rounded bg-slate-100" />
            <div className="h-3 rounded bg-slate-100" />
            <div className="h-3 rounded bg-slate-100" />
            <div className="h-3 rounded bg-slate-100" />
          </div>
        </div>
      ))}
    </div>
  );
}

export const primaryButton =
  'inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-emerald-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 disabled:opacity-50';
export const secondaryButton =
  'inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 disabled:opacity-50';
export const inputClass =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20';
